// src/services/supabase/blindOffers.js
// Servicios para gestión de ofertas ciegas (blind offers)

import { supabase } from '../../supabaseClient';
import { DEFAULT_SEMAPHORE_THRESHOLDS } from '../../config/blindBiddingConfig';

/**
 * Calcula el estado del semáforo de competitividad basado en % por debajo del precio
 * Verde: ≤3% debajo | Amarillo: 3-9% debajo | Rojo: >9% debajo (default)
 * @param {number} amount - Monto de la oferta ciega
 * @param {number} startPrice - Precio de salida de la propiedad
 * @param {Object} [thresholds={}] - Umbrales personalizados (green, yellow)
 * @param {number} [thresholds.green=3] - Umbral verde en %
 * @param {number} [thresholds.yellow=9] - Umbral amarillo en %
 * @returns {string} Estado del semáforo: 'green' | 'yellow' | 'red'
 */
export const calculateSemaphore = (amount, startPrice, thresholds = {}) => {
  const greenThreshold = thresholds.green ?? DEFAULT_SEMAPHORE_THRESHOLDS.green;
  const yellowThreshold = thresholds.yellow ?? DEFAULT_SEMAPHORE_THRESHOLDS.yellow;
  
  const percentageBelow = ((startPrice - amount) / startPrice) * 100;
  
  // Si está por encima o igual al precio de salida = verde
  if (amount >= startPrice) return 'green';
  
  // Si está menos del greenThreshold% por debajo = verde
  if (percentageBelow <= greenThreshold) return 'green';
  
  // Si está entre greenThreshold% y yellowThreshold% por debajo = amarillo
  if (percentageBelow <= yellowThreshold) return 'yellow';
  
  // Si está más del yellowThreshold% por debajo = rojo
  return 'red';
};

/**
 * Obtiene todas las ofertas ciegas de una propiedad ordenadas por monto (Admin)
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<Array>} Lista de ofertas ciegas con semáforo y estado verificación
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getPropertyBlindOffers = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('blind_offers')
      .select('*')
      .eq('property_id', propertyId)
      .order('amount', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) throw error;

    return data.map(offer => ({
      id: offer.id,
      propertyId: offer.property_id,
      inviteId: offer.invite_id,
      buyerName: offer.buyer_name,
      buyerEmail: offer.buyer_email,
      amount: parseFloat(offer.amount),
      conditionsText: offer.conditions_text,
      semaphoreStatus: offer.semaphore_status,
      isVerified: offer.is_verified,
      createdAt: offer.created_at,
      updatedAt: offer.updated_at,
      verifiedAt: offer.verified_at,
    }));
  } catch (error) {
    console.error('Error obteniendo ofertas ciegas:', error);
    throw error;
  }
};

/**
 * Obtiene la oferta ciega de un usuario específico para una propiedad
 * Usuario solo ve su propia oferta, no las de otros
 * @param {string} propertyId - UUID de la propiedad
 * @param {string} email - Email del comprador
 * @returns {Promise<Object|null>} Oferta ciega del usuario o null si no existe
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getUserBlindOffer = async (propertyId, email) => {
  try {
    const { data, error } = await supabase
      .from('blind_offers')
      .select('*')
      .eq('property_id', propertyId)
      .eq('buyer_email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No encontrado
      throw error;
    }
    
    return {
      id: data.id,
      propertyId: data.property_id,
      inviteId: data.invite_id,
      buyerName: data.buyer_name,
      buyerEmail: data.buyer_email,
      amount: parseFloat(data.amount),
      conditionsText: data.conditions_text,
      semaphoreStatus: data.semaphore_status,
      isVerified: data.is_verified,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      verifiedAt: data.verified_at,
    };
  } catch (error) {
    console.error('Error obteniendo oferta ciega del usuario:', error);
    throw error;
  }
};

/**
 * Crea o actualiza una oferta ciega (upsert automático por email)
 * Recalcula semáforo y marca como no verificada si se actualiza
 * @param {Object} offerData - Datos de la oferta
 * @param {string} offerData.propertyId - UUID de la propiedad
 * @param {string} offerData.inviteId - UUID de la invitación
 * @param {string} offerData.buyerName - Nombre del comprador
 * @param {string} offerData.buyerEmail - Email del comprador
 * @param {number} offerData.amount - Monto de la oferta
 * @param {string} [offerData.conditionsText] - Condiciones adicionales
 * @param {number} offerData.startPrice - Precio de salida (para calcular semáforo)
 * @param {Object} [offerData.thresholds] - Umbrales personalizados del semáforo
 * @returns {Promise<Object>} Oferta creada/actualizada con flag isNew
 * @throws {Error} Si hay error al crear/actualizar en Supabase
 */
export const upsertBlindOffer = async (offerData) => {
  try {
    const {
      propertyId,
      inviteId,
      buyerName,
      buyerEmail,
      amount,
      conditionsText,
      startPrice,
      thresholds,
    } = offerData;

    // Calcular semáforo
    const semaphoreStatus = calculateSemaphore(amount, startPrice, thresholds);

    // Intentar actualizar primero (si existe)
    const { data: existing } = await supabase
      .from('blind_offers')
      .select('id')
      .eq('property_id', propertyId)
      .eq('buyer_email', buyerEmail)
      .single();

    let result;

    if (existing) {
      // Actualizar oferta existente
      const { data, error } = await supabase
        .from('blind_offers')
        .update({
          amount,
          conditions_text: conditionsText || null,
          semaphore_status: semaphoreStatus,
          is_verified: false, // Requiere re-verificación
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      result = data;
    } else {
      // Crear nueva oferta
      const { data, error } = await supabase
        .from('blind_offers')
        .insert({
          property_id: propertyId,
          invite_id: inviteId,
          buyer_name: buyerName,
          buyer_email: buyerEmail,
          amount,
          conditions_text: conditionsText || null,
          semaphore_status: semaphoreStatus,
          is_verified: false,
        })
        .select()
        .single();

      if (error) throw error;
      result = data;
    }

    return {
      id: result.id,
      propertyId: result.property_id,
      inviteId: result.invite_id,
      buyerName: result.buyer_name,
      buyerEmail: result.buyer_email,
      amount: parseFloat(result.amount),
      conditionsText: result.conditions_text,
      semaphoreStatus: result.semaphore_status,
      isVerified: result.is_verified,
      createdAt: result.created_at,
      updatedAt: result.updated_at,
      isNew: !existing,
    };
  } catch (error) {
    console.error('Error creando/actualizando oferta ciega:', error);
    throw error;
  }
};

/**
 * Marca una oferta ciega como verificada (confirmada por PIN u otro método)
 * @param {string} offerId - UUID de la oferta ciega
 * @returns {Promise<Object>} Oferta con is_verified=true y verified_at timestamp
 * @throws {Error} Si hay error al actualizar en Supabase
 */
export const verifyBlindOffer = async (offerId) => {
  try {
    const { data, error } = await supabase
      .from('blind_offers')
      .update({
        is_verified: true,
        verified_at: new Date().toISOString(),
      })
      .eq('id', offerId)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      isVerified: data.is_verified,
      verifiedAt: data.verified_at,
    };
  } catch (error) {
    console.error('Error verificando oferta ciega:', error);
    throw error;
  }
};

/**
 * Contar ofertas ciegas verificadas de una propiedad
 * @param {string} propertyId - ID de la propiedad
 * @returns {Promise<number>} Número de ofertas verificadas
 */
export const countVerifiedBlindOffers = async (propertyId) => {
  try {
    const { count, error } = await supabase
      .from('blind_offers')
      .select('*', { count: 'exact', head: true })
      .eq('property_id', propertyId)
      .eq('is_verified', true);

    if (error) throw error;
    
    return count || 0;
  } catch (error) {
    console.error('Error contando ofertas ciegas:', error);
    return 0;
  }
};

/**
 * Obtener la mejor oferta ciega verificada (para transición a OPEN)
 * @param {string} propertyId - ID de la propiedad
 * @returns {Promise<object|null>} Mejor oferta o null
 */
export const getBestBlindOffer = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .from('blind_offers')
      .select('*')
      .eq('property_id', propertyId)
      .eq('is_verified', true)
      .order('amount', { ascending: false })
      .order('created_at', { ascending: true })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return {
      id: data.id,
      propertyId: data.property_id,
      buyerName: data.buyer_name,
      buyerEmail: data.buyer_email,
      amount: parseFloat(data.amount),
      conditionsText: data.conditions_text,
      semaphoreStatus: data.semaphore_status,
    };
  } catch (error) {
    console.error('Error obteniendo mejor oferta ciega:', error);
    return null;
  }
};

/**
 * Activa la fase BLIND_ACTIVE iniciando el contador
 * @param {string} propertyId - UUID de la propiedad
 * @param {string} deadline - Deadline final de fase ciega (ISO string)
 * @returns {Promise<Object>} Propiedad con blind_bidding_started_at y deadline
 * @throws {Error} Si hay error al actualizar en Supabase
 */
export const activateBlindBiddingPhase = async (propertyId, deadline) => {
  try {
    const now = new Date();
    const deadlineDate = new Date(deadline);

    const { data, error } = await supabase
      .from('properties')
      .update({
        blind_bidding_started_at: now.toISOString(),
        blind_bidding_deadline: deadlineDate.toISOString(),
      })
      .eq('id', propertyId)
      .select()
      .single();

    if (error) throw error;

    console.log(`✅ Fase BLIND_ACTIVE activada para propiedad ${propertyId}, deadline: ${deadlineDate.toISOString()}`);
    return data;
  } catch (error) {
    console.error('Error activando fase blind bidding:', error);
    throw error;
  }
};